import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser, StructureTrancheMap, StructureTranches } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface TeaserTrancheFormProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const TeaserTrancheForm: React.FC<TeaserTrancheFormProps> = ({ dataRoomGuid, teaser, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();

	const trancheMutation = useMutation(
		async ({ values }: TeaserTrancheFormMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoomTeaser>(
				api.patch(CerebroApiEndpoints.Teasers.Specific(dataRoomGuid, teaser?.guid), {
					...values,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Teaser was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={teaser != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				{teaser?.firstName} {teaser?.lastName}
			</CerebroHeading>
			<Formik
				initialValues={{
					structureType: teaser?.structureType,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(trancheMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={12}>
									<CerebroLabel id="structureType">Tranche</CerebroLabel>
									<Field
										id="structureType"
										as={CerebroDropdown}
										name="structureType"
										disabled={isSubmitting}
									>
										{Object.entries(StructureTrancheMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>

								<Grid item>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type TeaserTrancheFormMutationVariables = FormikUtils.MutateOnSubmitVariables<TeaserTrancheFormValues>;
interface TeaserTrancheFormValues {
	structureType: StructureTranches | null | undefined;
}
type TeaserTrancheFormValueErrors = FormikErrors<TeaserTrancheFormValues>;
